import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Save, Upload, X, Palette, Type, Image, Check } from 'lucide-react';
import { FeaturedLayout } from '../layouts';
import { Button, buttonVariants } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { ColorPicker } from '../ui/color-picker';
import { getPlatformConfig, updatePlatformConfig, uploadPlatformAsset } from '../../lib/supabase';

const formFromConfig = (data) => ({
  platform_name: data?.platform_name || 'MYKLUBB',
  login_subtitle: data?.login_subtitle || 'Control',
  login_color_bg: data?.login_color_bg || '#000000',
  login_color_text: data?.login_color_text || '#F2F2F3',
  login_color_accent: data?.login_color_accent || '#B5B8BE',
});

const PlatformBranding = () => {
  const messageRef = useRef(null);

  const [form, setForm] = useState(formFromConfig(null));
  const [savedForm, setSavedForm] = useState(formFromConfig(null));
  const [currentConfig, setCurrentConfig] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [faviconPreview, setFaviconPreview] = useState(null);
  const [faviconFile, setFaviconFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  const hasChanges = useMemo(() => {
    if (logoFile || faviconFile) return true;
    return Object.keys(form).some(key => form[key] !== savedForm[key]);
  }, [form, savedForm, logoFile, faviconFile]);

  useEffect(() => {
    const load = async () => {
      const { data } = await getPlatformConfig();
      if (data) {
        setCurrentConfig(data);
        const initial = formFromConfig(data);
        setForm(initial);
        setSavedForm(initial);
      }
      setLoading(false);
    };
    load();
  }, []);

  const handleChange = (field, value) => {
    setMessage(null);
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleFileSelect = (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'El archivo no debe superar 2 MB' });
      return;
    }

    const preview = URL.createObjectURL(file);
    if (type === 'logo') {
      setLogoFile(file);
      setLogoPreview(preview);
    } else {
      setFaviconFile(file);
      setFaviconPreview(preview);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);

    try {
      const updates = { ...form };

      if (logoFile) {
        const { url, error } = await uploadPlatformAsset(logoFile, 'login-logo');
        if (error) throw error;
        updates.login_logo_url = url;
      }

      if (faviconFile) {
        const { url, error } = await uploadPlatformAsset(faviconFile, 'favicon');
        if (error) throw error;
        updates.favicon_url = url;
      }

      const { data, error } = await updatePlatformConfig(updates);
      if (error) throw error;

      setCurrentConfig(data);
      const updated = formFromConfig(data);
      setForm(updated);
      setSavedForm(updated);
      setLogoFile(null);
      setLogoPreview(null);
      setFaviconFile(null);
      setFaviconPreview(null);
      setMessage({ type: 'success', text: 'Listo. Los cambios se guardaron.', ts: Date.now() });
      setTimeout(() => messageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100);
    } catch (err) {
      console.error('Error saving platform config:', err);
      setMessage({ type: 'error', text: `Algo salio mal: ${err.message}`, ts: Date.now() });
      setTimeout(() => messageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <FeaturedLayout title="Branding de Plataforma">
        <div className="p-6 text-center text-muted-foreground">Cargando...</div>
      </FeaturedLayout>
    );
  }

  return (
    <FeaturedLayout title="Branding de Plataforma">
      <div className="max-w-2xl mx-auto flex flex-col gap-6">

        {message && (
          <Alert
            ref={messageRef}
            key={message.ts}
            variant={message.type === 'success' ? 'success' : 'destructive'}
            className="animate-in fade-in duration-300"
          >
            <AlertDescription>{message.text}</AlertDescription>
          </Alert>
        )}

        {/* Textos */}
        <div className="bg-background rounded-xl border p-6 flex flex-col gap-4">
          <div className="flex items-center gap-2 text-foreground font-semibold text-lg">
            <Type />
            Textos
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="platform_name">Nombre de la plataforma</Label>
              <Input
                id="platform_name"
                value={form.platform_name}
                onChange={(e) => handleChange('platform_name', e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="login_subtitle">Subtitulo en login</Label>
              <Input
                id="login_subtitle"
                value={form.login_subtitle}
                onChange={(e) => handleChange('login_subtitle', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Logo y Favicon */}
        <div className="bg-background rounded-xl border p-6 flex flex-col gap-4">
          <div className="flex items-center gap-2 text-foreground font-semibold text-lg">
            <Image />
            Logo y Favicon
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Logo del login */}
            <div className="flex flex-col gap-2">
              <Label>Logo del login</Label>
              <div className="flex items-center gap-4">
                <div className="size-16 shrink-0 bg-gray-900 rounded-xl flex items-center justify-center overflow-hidden">
                  {logoPreview ? (
                    <img src={logoPreview} alt="Preview" className="size-12 object-contain" />
                  ) : currentConfig?.login_logo_url ? (
                    <img src={currentConfig.login_logo_url} alt="Logo" className="size-12 object-contain" />
                  ) : (
                    <img src="/assets/myklubb-icon.svg" alt="Default" className="size-12 object-contain"
                      onError={(e) => { e.target.src = '/assets/raiders-trc-icon.svg' }} />
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  <label className={buttonVariants({ variant: 'secondary', size: 'sm', className: 'cursor-pointer' })}>
                    <Upload className="size-4 shrink-0" />
                    {logoPreview ? 'Cambiar' : 'Subir'}
                    <input type="file" accept="image/svg+xml,image/png,image/jpeg,image/webp"
                      onChange={(e) => handleFileSelect(e, 'logo')} className="sr-only" />
                  </label>
                  {logoPreview && (
                    <Button variant="link" size="sm" className="text-destructive h-auto p-0 justify-start"
                      onClick={() => { setLogoFile(null); setLogoPreview(null); }}>
                      <X data-icon="inline-start" />
                      Cancelar
                    </Button>
                  )}
                  <p className="text-xs text-muted-foreground">SVG o PNG, max 2 MB</p>
                </div>
              </div>
            </div>

            {/* Icono de la app (favicon + PWA) */}
            <div className="flex flex-col gap-2">
              <Label>Icono de la app</Label>
              <div className="flex items-center gap-4">
                <div className="size-16 shrink-0 bg-muted rounded-xl flex items-center justify-center overflow-hidden border">
                  {faviconPreview ? (
                    <img src={faviconPreview} alt="Preview" className="size-12 object-contain" />
                  ) : currentConfig?.favicon_url ? (
                    <img src={currentConfig.favicon_url} alt="Favicon" className="size-12 object-contain" />
                  ) : (
                    <span className="text-muted-foreground text-xs">512x512</span>
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  <label className={buttonVariants({ variant: 'secondary', size: 'sm', className: 'cursor-pointer' })}>
                    <Upload className="size-4 shrink-0" />
                    {faviconPreview ? 'Cambiar' : 'Subir'}
                    <input type="file" accept="image/png,image/svg+xml"
                      onChange={(e) => handleFileSelect(e, 'favicon')} className="sr-only" />
                  </label>
                  {faviconPreview && (
                    <Button variant="link" size="sm" className="text-destructive h-auto p-0 justify-start"
                      onClick={() => { setFaviconFile(null); setFaviconPreview(null); }}>
                      <X data-icon="inline-start" />
                      Cancelar
                    </Button>
                  )}
                  <p className="text-xs text-muted-foreground leading-snug">
                    PNG cuadrado 512x512 con fondo solido. Se usa como favicon, apple-touch-icon, y icono al instalar la app en movil.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Colores del login */}
        <div className="bg-background rounded-xl border p-6 flex flex-col gap-4">
          <div className="flex items-center gap-2 text-foreground font-semibold text-lg">
            <Palette />
            Colores del login
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <ColorPicker
              label="Fondo"
              value={form.login_color_bg}
              onChange={(v) => handleChange('login_color_bg', v)}
            />
            <ColorPicker
              label="Texto"
              value={form.login_color_text}
              onChange={(v) => handleChange('login_color_text', v)}
            />
            <ColorPicker
              label="Acento"
              value={form.login_color_accent}
              onChange={(v) => handleChange('login_color_accent', v)}
            />
          </div>

          {/* Preview del login */}
          <div className="flex flex-col gap-2">
            <Label>Vista previa del login</Label>
            <div className="rounded-xl p-6 flex flex-col items-center gap-3"
                 style={{ backgroundColor: form.login_color_bg }}>
              <div className="size-14 rounded-xl overflow-hidden flex items-center justify-center">
                {logoPreview || currentConfig?.login_logo_url ? (
                  <img src={logoPreview || currentConfig?.login_logo_url} alt="" className="size-12 object-contain" />
                ) : (
                  <img src="/assets/myklubb-icon.svg" alt="" className="size-12 object-contain"
                    onError={(e) => { e.target.src = '/assets/raiders-trc-icon.svg' }} />
                )}
              </div>
              <div className="text-xl font-bold tracking-wider" style={{ color: form.login_color_text }}>
                {form.platform_name}
              </div>
              <div className="text-sm" style={{ color: form.login_color_accent }}>
                {form.login_subtitle}
              </div>
            </div>
          </div>
        </div>

        {/* Guardar */}
        <div className="flex justify-end pb-6">
          <Button size="lg" onClick={handleSave} disabled={saving || !form.platform_name.trim() || !hasChanges}>
            {saving ? (
              <>
                <Save data-icon="inline-start" className="animate-spin" />
                Guardando...
              </>
            ) : !hasChanges ? (
              <>
                <Check data-icon="inline-start" />
                Sin cambios
              </>
            ) : (
              <>
                <Save data-icon="inline-start" />
                Guardar cambios
              </>
            )}
          </Button>
        </div>
      </div>
    </FeaturedLayout>
  );
};

export default PlatformBranding;
